import { useMemo, type FC, useState } from 'react';
import type { HeroCoachProps } from './types';
import { shade } from 'shared/helpers/utils';

import styled, { css } from 'styled-components';

import {
  ApiService,
  COLOR_YELLOW,
  baseMediaEndpoint,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Wrapper, Button, Mockup, Download } from '../Common';
import { ConditionalImage } from '../ConditionalImage';
import { Image as ImageComp } from 'shared/components/Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Coaching = styled.div`
  position: relative;
  padding: 103px 0 123px;
  background: #fff;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 83px 0;
`}

  ${({ theme: { $width } }) => `
    ${
      $width <= 768 &&
      `
      padding: 73px 0 63px;
    `
    }
  `}

  ${({ theme: { $width } }) => `
    ${
      $width <= 514 &&
      `
      padding: 193px 0 63px;
    `
    }
  `}
`;

const StyledWrapper = styled(Wrapper)`
  position: relative;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* gap: 40px; */

  ${({ theme: { $width } }) => `
    ${
      $width <= 991 &&
      `
      flex-direction: column-reverse;
      align-items: center;
    `
    }
  `}
`;

const TitleInner = styled.div`
  width: 580px;

  ${({ theme: { $width } }) => `
    ${
      $width <= 1254 &&
      `
      width: 460px;
    `
    }
    ${
      $width <= 991 &&
      `
      margin-top: 30px;
      width: 100%;
      text-align: center;
    `
    }
    
  `}
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  margin-bottom: 35px;
  position: relative;
  width: 100%;
  flex-shrink: 0;

  ${({ theme: { $width } }) => `
    ${
      $width <= 991 &&
      `
      margin-bottom: 15px;
    `
    }
    ${
      $width <= 575 &&
      `
      margin-bottom: 20px;
    `
    }
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const SubtitleBot = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  line-height: 1.4em;
  margin-bottom: 72px;
  position: relative;

  ${({ theme: { $width } }) => `
    ${
      $width <= 1254 &&
      `
      margin-bottom: 70px;
    `
    }
    ${
      $width <= 991 &&
      `
      margin-bottom: 60px;
    `
    }
    ${
      $width <= 575 &&
      `
      margin-bottom: 40px;
    `
    }
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BtnInner = styled.div`
  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
  display: flex;
  justify-content: center;
`}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 415px;
  display: inline-block;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding-top: 24px;
      padding-bottom: 26px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const ImageInner = styled.div`
  display: flex;
  position: relative;
  width: 660px;
  height: 660px;
  border-radius: 50%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 510px;
    height: 100%;
`}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    height: 100%;
`}
`;

const MockUpStyled = styled((props) => <Mockup {...props} />)`
  width: 480px;
  height: auto;
  position: absolute;
  z-index: 12;
  top: -5%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 380px;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 300px;
`}


  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 320px;
`}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 80%;
    `}

   ${({ mockup_id, theme: { $width } }) =>
    mockup_id === 3
      ? `width:400px; 

      ${
        $width <= 1254 &&
        `
        width: 309px;
        left: 50px;
      `
      }

      ${
        $width <= 991 &&
        `
        width: 247px;
        left: 50px;
      `
      }

      ${
        $width <= 768 &&
        `
        width: 263px;
        left: 0px;
      `
      }

      ${
        $width <= 575 &&
        `
        width: 70%;
        margin-bottom: 30px;
        left: 30px;
      `
      }
      ${
        $width <= 390 &&
        `
        left: 10px;
      `
      }
        `
      : mockup_id === 2
        ? `
      ${
        $width <= 1254 &&
        `
        width: 363px;
      `
      }

      ${
        $width <= 991 &&
        `
        width: 293px
      `
      }

      ${
        $width <= 768 &&
        `
        width: 313px
      `
      }

      ${
        $width <= 575 &&
        `
        left: -3%;
      `
      }
      ${
        $width <= 390 &&
        `
        width: 263px;
      `
      }
      ${
        $width <= 326 &&
        `
        width: 213px;
      `
      }
      `
        : mockup_id === 1
          ? `

      ${
        $width <= 326 &&
        `
        left: 0px;
      `
      }
      `
          : ``}
`;

const PhotoInner = styled.div<{ editable?: boolean }>`
  position: absolute;
  z-index: ${(props) => (props.editable ? 21 : 40)};
  right: 0%;
  bottom: 1%;
  width: 265px;
  height: 265px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 170px;
    height: 170px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    right: 0;
    width: 200px;
    height: 200px;
    `}
`;

const photoImages = css`
  width: 100% !important;
  height: 100% !important;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    object-position: center center;
  }
`;

const PhotoImage = styled(ImageComp)<{ borderColor: string }>`
  border-radius: 50%;
  border: 10px solid;
  border-color: ${(props) => props.borderColor};
`;

const Arrow = styled.svg`
  position: absolute;
  z-index: 2;
  width: 210px;
  height: 50px;
  bottom: 1%;
  left: 21%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
  width: 145px;
  height: auto;
  bottom: 3%;
  left: 24%;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 95px;
    height: auto;
    bottom: 3%;
    bottom: -2%;
    left: 50%;
    display: none;
`}
${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    display: none;
`}
`;

const OwnerText = styled.div`
  position: absolute;
  right: 50%;
  bottom: 45px;
  width: 180px;
  text-align: center;
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.4em;
  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
  right: 38%;
  bottom: 18px;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    bottom: 9px;
    width: 172px;
`}

${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    right: 0;
    left: 0;
    margin: 0 auto;
    width: 100%;
    bottom: -9px;
`}
`;

const ImgInner = styled.div<{ editable?: boolean }>`
  width: 68%;
  position: relative;
  z-index: 19;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    z-index: ${(props) => (props.editable ? 50 : 19)};
  }

  @media screen and (max-width: 552px) {
    width: 80%;
  }
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
`;

const HoverableDropzoneStyled = styled(ConditionalImage)<{
  width: number;
  height: number;
}>`
  max-width: 100%;
  max-height: 670px;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto !important;
  aspect-ratio: ${({ width, height }) => (width && height ? `${width} / ${height}` : '513 / 685')};
  object-fit: cover;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 991px) {
    width: 100% !important;
    height: auto !important;
    aspect-ratio: ${({ width, height }) =>
      width && height ? `${width} / ${height}` : '513 / 685'};
    object-fit: cover;
  }
`;

const BookMediaImg = styled(ImageComp)`
  min-width: 100%;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  object-position: center center;
`;

const WhiteBgCircle = styled.div`
  width: 100%;
  height: auto;
  border-radius: 50%;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  aspect-ratio: 1 / 1;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    top: auto;
    bottom: 0;
`}// @media screen and (max-width: 575px) {
  //   top: auto;
  //   bottom: 0;
  // }
`;

export const V2: FC<HeroCoachProps> = (props) => {
  const {
    secondaryColor = COLOR_YELLOW,
    backgroundColor = COLOR_YELLOW,
    customBackgroundColor,
    oppositeBackgroundColor,
    mockup,
    button = defaultWebValue,
    title = defaultWebValue,
    content = defaultWebValue,
    owner = defaultWebValue,
    textColor,
    hidden,
    meta = {},
    cover_thumbnail,
    order
  } = props;

  const {
    salesPageId,
    updateImageMeta,
    pagesThumbnails,
    purchaseElementId,
    isWebsiteOnly,
    removeMetaTag,
    type,
    isDashboard
  } = useSales();

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { pages, isCoverDisabled, bookMedia, bookId } = useBook();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const [isLoading, setIsLoading] = useState([false, false]);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, cover_thumbnail, pagesThumbnails]);

  const youHereImage = useMemo(() => {
    if (meta['you_here']) {
      if (baseMediaEndpoint?.includes('localhost') && !meta['you_here'].includes('localhost')) {
        return `${baseMediaEndpoint}${meta['you_here']}`;
      }
      return meta['you_here'];
    }
    return '/img/you_here.png';
  }, [meta]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const shadedBackgroundColor = useMemo(() => {
    return shade(secondaryColor, 0.85);
  }, [secondaryColor]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || '#fff';
  }, [customBackgroundColor]);

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const photoAccessKey = useMemo(() => generateAccessKey('hp', order), [order]);
  const ownerAccessKey = useMemo(() => generateAccessKey('ho', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const circleBackgroundColor = useMemo(() => {
    return oppositeBackgroundColor || shadedBackgroundColor;
  }, [oppositeBackgroundColor, shadedBackgroundColor]);

  const arrowColor = useMemo(() => {
    return filterBackgroundColor(resolvedBackgroundColor, textColor);
  }, [resolvedBackgroundColor, textColor]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File, type: string, index: number) => {
    try {
      setIsLoading((prev) => prev.map((_, i) => (i === index ? true : false)));
      if (!meta['accessorKey'] || !editable) return;
      const formData = new FormData();
      if (isWebsiteOnly) formData.append('name', 'book_mockup');
      else formData.append('type', meta['accessorKey']);
      removeMetaTag(meta['accessorKey'], 'image_placeholder');
      formData.append('file', file);
      if (index === 0) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            setResize({ width, height });
            // updateImageMeta(meta['accessorKey'], width, 'width');
            // updateImageMeta(meta['accessorKey'], height, 'height');
          };
          img.src = e.target?.result as string;
        };

        reader.readAsDataURL(file);
      }

      if (isWebsiteOnly) {
        formData.append('file', file);
        const response = await ApiService.post(
          `/book-generator/books/${bookId}/book_media/`,
          formData
        );
        updateImageMeta(meta['accessorKey'], response.data.file, 'image');
      } else {
        const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
        updateImageMeta(meta['accessorKey'], response.data.file, type);
      }

      setIsLoading([false, false]);
    } catch (error) {
      handleUploadError(error);
      setIsLoading([false, false]);
    }
  };

  return (
    <Coaching
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: resolvedBackgroundColor
      }}
      aria-label="Hero coaching section with circular design and author photo"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero coaching content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleInner id="CoachingContentSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim()
                      ? 'Hero coaching section main title'
                      : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this coaching title`,
                  tabIndex: 0
                })}
          />

          <SubtitleBot
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim()
                      ? 'Coaching section description'
                      : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this coaching description`,
                  tabIndex: 0
                })}
          />

          <BtnInner role="group" aria-label={`Coaching action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            )}
          </BtnInner>
        </TitleInner>

        <ImageInner role="group" aria-label="Circular design with book preview and author photo">
          <ImgInner editable={editable} role="img" aria-label="Book mockup and preview">
            {bookMockup ? (
              <HoverableDropzoneStyled
                width={resize.width}
                height={resize.height}
                isSalePage
                css={imageStyles as never}
                disabled={!editable}
                onUpload={(file) => handleUpload(file, 'image', 0)}
                loading={isLoading[0]}
                alreadyUploaded={Boolean(bookMockup)}
                enableLinks
                updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
                imageFile={bookMockup}
                resizeSelector={editable}
                handleResize={({ width, height }) => {
                  setResize({ width, height });
                  updateImageMeta(meta['accessorKey'], width, 'width');
                  updateImageMeta(meta['accessorKey'], height, 'height');
                }}
                initialPosition={{
                  x: meta['x_1'] || 0,
                  y: meta['y_1'] || 0,
                  width: meta['d_width_1'],
                  height: meta['d_height_1']
                }}
                meta={meta}
                positionEnabled
                handlePosition={(width: number, height: number, x: number, y: number) => {
                  updateImageMeta(meta['accessorKey'], x, 'x_1');
                  updateImageMeta(meta['accessorKey'], y, 'y_1');
                  updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                  updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                }}
                imageAspectRatio={{
                  ratio: '3x4',
                  pixels:
                    resize.width && resize.height ? `${resize.width}x${resize.height}` : '600x800'
                }}
                ariaLabel="Book mockup image - drag and drop to upload new image">
                <BookMediaImg
                  src={bookMockup}
                  width={resize.width || 500}
                  height={resize.height || 500}
                  priority
                  alt="Book mockup preview showing cover design"
                />
              </HoverableDropzoneStyled>
            ) : (
              <MockUpStyled
                mockup={mockup?.file}
                mockup_id={mockup?.id}
                cover_thumbnail={coverThumbnail}
                aria-label="Book mockup with cover thumbnail"
              />
            )}
          </ImgInner>

          <PhotoInner
            role="img"
            aria-label="Author profile photo"
            {...(!isDashboard && {
              accessKey: photoAccessKey,
              title: `Press Alt+${photoAccessKey} to focus the author photo`,
              tabIndex: 0
            })}>
            <ConditionalImage
              isSalePage
              css={photoImages as never}
              disabled={!editable}
              onUpload={(file) => handleUpload(file, 'you_here', 1)}
              loading={isLoading[1]}
              alreadyUploaded={Boolean(meta['you_here'])}
              imageFile={meta['you_here']}
              // imageAspectRatio={{
              //   ratio: '1x1',
              //   pixels: '1000x1000'
              // }}
              // resizeSelector={editable}
              // handleResize={({ width, height }) => {
              //   setResize({ width, height });
              //   updateImageMeta(meta['accessorKey'], width, 'width');
              //   updateImageMeta(meta['accessorKey'], height, 'height');
              // }}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              size="sm"
              ariaLabel="Author photo - drag and drop to upload your photo">
              <PhotoImage
                borderColor={backgroundColor}
                src={youHereImage}
                alt="Author profile photo in circular frame"
                width={500}
                height={500}
              />
            </ConditionalImage>
          </PhotoInner>

          <Arrow
            viewBox="0 0 214 55"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            role="presentation"
            focusable="false"
            aria-label="Decorative arrow pointing to author photo">
            <path
              d="M8.43604 3.03003C7.45862 7.74938 -6.03494 42.3986 10.4097 45.1644C22.9429 47.2723 35.9089 47.8807 48.7322 48.1012C85.1737 48.728 122.244 47.103 158.436 44.0256C160.191 43.8764 200.18 40.6223 199.062 37.9722C197.504 34.2841 190.683 29.0981 186.068 27.6034C180.927 25.938 180.254 26.2501 187.137 27.963C194.345 29.7568 202.092 31.8301 209.752 32.1585C216.278 32.4382 202.564 41.2728 201.529 41.9878C196.648 45.3574 191.466 50.0336 186.068 52.6563"
              stroke={arrowColor}
              strokeOpacity="0.75"
              strokeWidth="4.5"
              strokeLinecap="round"
            />
          </Arrow>

          <OwnerText
            aria-label="Author name and title"
            {...(!isDashboard && {
              accessKey: ownerAccessKey,
              title: `Press Alt+${ownerAccessKey} to focus the author information`,
              tabIndex: 0
            })}>
            <div
              id={owner.id}
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(owner.val) }}
              style={{ color: filterBackgroundColor(secondaryColor, textColor) }}
              {...(isDashboard
                ? {
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      try {
                        updateActiveElement(e);
                      } catch (error) {
                        handleEditError(error);
                      }
                    },
                    onBlur: resetActiveElement,
                    'data-accessor-parent': meta['accessorKey'],
                    'data-accessorkey': 'owner'
                  }
                : {
                    'aria-label': !owner.val?.trim() ? 'Author name and credentials' : undefined
                  })}
            />
          </OwnerText>

          <WhiteBgCircle
            aria-hidden="true"
            role="presentation"
            style={{ background: circleBackgroundColor }}
          />
        </ImageInner>
      </StyledWrapper>
    </Coaching>
  );
};

V2.displayName = 'HeroCoachingV2';
